import { DashboardLayout } from "components/DashboardLayout";
import { Header } from "components/DesignSystem/Header/Header";
import { useCurrentEntityId } from "hooks/useCurrentEntityId";
import { EntitySelector } from "components/EntitySelector/EntitySelector";
import Tab from "components/DesignSystem/Tab/Tab";
import {
  BOOKS_SETTINGS_TABS,
  BOOKS_SETTINGS_TAB_LABELS,
} from "constants/booksSettings";
import { useQuery, useUpdateQuery } from "hooks/useQuery";
import { AccountingSettings } from "./AccountingSettings";
import { RuleEngine } from "components/RuleEngine/RuleEngine";
import { Divider } from "components/design/Divider";
import { DefaultCategories } from "./DefaultCategories";
import { InvoiceSettings } from "./InvoiceSettings";
import { BillsSettings } from "./BillsSettings";
import { CustomReportsSettings } from "./CustomReportsSettings";
import { ChartOfAccounts } from "pages/Books/Accounting/ChartOfAccounts";
import ToolTip from "components/design/toolTip";
import { SmallInfo } from "components/icons/SmallInfo";
import classNames from "classnames";
import DashboardContainer from "components/dashboard/DashboardContainer";
import { ClassificationsSettings } from "./ClassificationsSettings";
import { DiamondPurple } from "components/icons/DiamondPurple";
import { BooksSoftwareSubscriptionsModal } from "components/BooksSoftwareSubscriptionsModal";
import { ModalDescriptionWrapper } from "pages/Books/RevenueMetrics/RevenueMetricsLocked";
import { Button } from "components/DesignSystem/Button/Button";
import Wind from "components/icons/Wind";
import { useModal } from "hooks/useModal";
import { CLASSIFICATION_FEATURE_ACCESS } from "constants/subscriptionPermissionFeatures";
import { useHasSubscriptionPermission } from "hooks/useHasSubscriptionPermission";
import { PermissionBasedUI } from "components/PermissionBasedUI/PermissionBasedUI";
import { Tag } from "components/DesignSystem/Tag/Tag";
import { useMyPermissions } from "hooks/useMyPermissions";
import { useRoleBasedView } from "hooks/useRoleBasedView";

export const SectionHeader = ({
  icon,
  title,
  toolTipText,
}: {
  icon: React.ReactNode;
  title: string;
  toolTipText?: string;
}) => {
  return (
    <div className="t-flex t-items-center t-flex-row t-gap-2">
      <div className="t-flex t-text-purple-40 t-bg-surface-purple t-p-1 t-items-center t-border-0 t-rounded-[4px]">
        {icon}
      </div>
      <div className="t-text-subtext t-text-text-30 t-flex t-gap-1">
        <div>{title}</div>
        {toolTipText && (
          <ToolTip text={toolTipText}>
            <div>
              <SmallInfo className="t-size-4" />
            </div>
          </ToolTip>
        )}
      </div>
    </div>
  );
};

export const SettingRow = ({
  title,
  description,
  info,
  children,
  align = "center",
}: {
  title?: string;
  description?: string;
  info?: React.ReactNode;
  children: React.ReactNode;
  align?: "top" | "center";
}) => {
  return (
    <>
      <div
        className={classNames(
          "t-flex t-justify-between t-gap-4 t-mt-5 t-mb-5 first:t-mt-0 sm:t-flex-col sm:t-items-start sm:t-gap-3",
          {
            "t-items-center": align === "center",
            "t-items-start": align === "top",
          }
        )}
      >
        <div className="t-flex t-gap-1 t-flex-col t-flex-1">
          {title && (
            <div className="t-text-subtext t-text-text-60">{title}</div>
          )}
          {description && (
            <p className="t-text-body-sm-lite t-text-text-30 t-mb-0 t-w-[75%] sm:t-w-full">
              {description}
            </p>
          )}
          {info && <div>{info}</div>}
        </div>
        <div className="t-flex t-items-center sm:t-w-full">{children}</div>
      </div>
      <div className="last:t-invisible">
        <Divider color="light-grey" />
      </div>
    </>
  );
};

export const BooksSettings = ({
  showEntitySelector = true,
}: {
  showEntitySelector?: boolean;
}) => {
  const entityId = useCurrentEntityId();
  const query = useQuery();
  const { update } = useUpdateQuery();
  const subscriptionModal = useModal();

  const {
    isSuperAdmin,
    isLoading: permissionsLoading,
    permissions,
  } = useMyPermissions();
  const { isCustomer } = useRoleBasedView();

  const canSeeTab = (key: string) => {
    if (!isCustomer || isSuperAdmin) return true;
    if (permissionsLoading) return true;
    const perm = permissions.find((p) => p.permission_key === key);
    return Boolean(perm) && perm!.access_level !== "NO_ACCESS";
  };

  const canSeeAccounting = true;
  const canSeeRules = canSeeTab("TRANSACTIONS");
  const canSeeDefaultCategories = canSeeTab("ACCOUNTING");
  const canSeeChartOfAccounts = canSeeTab("ACCOUNTING");
  const canSeeClassifications = canSeeTab("TRANSACTIONS");
  const canSeeInvoices = canSeeTab("REVENUE");
  const canSeeBills = canSeeTab("EXPENSE");

  const tabAccessMap: Record<string, boolean> = {
    [BOOKS_SETTINGS_TABS.ACCOUNTING]: canSeeAccounting,
    [BOOKS_SETTINGS_TABS.RULES]: canSeeRules,
    [BOOKS_SETTINGS_TABS.DEFAULT_CATEGORIES]: canSeeDefaultCategories,
    [BOOKS_SETTINGS_TABS.CHART_OF_ACCOUNTS]: canSeeChartOfAccounts,
    [BOOKS_SETTINGS_TABS.CLASSIFICATIONS]: canSeeClassifications,
    [BOOKS_SETTINGS_TABS.INVOICES]: canSeeInvoices,
    [BOOKS_SETTINGS_TABS.BILLS]: canSeeBills,
  };

  const firstAccessibleTab =
    Object.entries(tabAccessMap).find(([, accessible]) => accessible)?.[0] ??
    BOOKS_SETTINGS_TABS.ACCOUNTING;

  const requestedTab = query.get("tab") || BOOKS_SETTINGS_TABS.ACCOUNTING;
  const activeTab =
    !permissionsLoading && !tabAccessMap[requestedTab]
      ? firstAccessibleTab
      : requestedTab;

  const classificationPermission = useHasSubscriptionPermission({
    feature: CLASSIFICATION_FEATURE_ACCESS,
    product_type: "BOOKS",
    parentEntityId: entityId,
  });

  const isClassificationBlocked = classificationPermission?.blocked;

  const handleTabChange = (value: string) => {
    update({ query: "tab", value });
  };

  return (
    <>
      <Tab.Root value={activeTab} onValueChange={handleTabChange} asChild>
        <DashboardContainer className="t-h-full t-grow-0 t-shrink t-overflow-x-auto t-relative sm:t-h-auto sm:t-overflow-x-visible">
          <DashboardContainer.Header className="t-px-10 t-z-header">
            <Header
              v2
              title={
                BOOKS_SETTINGS_TAB_LABELS[
                  activeTab as keyof typeof BOOKS_SETTINGS_TAB_LABELS
                ]
              }
              right={showEntitySelector ? <EntitySelector /> : null}
            />
          </DashboardContainer.Header>
          <DashboardContainer.Content className="sm:t-overflow-visible">
            <Tab.Content
              value={BOOKS_SETTINGS_TABS.ACCOUNTING}
              className="t-px-5"
            >
              <AccountingSettings entityId={entityId!} />
            </Tab.Content>
            <Tab.Content value={BOOKS_SETTINGS_TABS.RULES} className="t-h-full">
              <RuleEngine />
            </Tab.Content>
            <Tab.Content
              value={BOOKS_SETTINGS_TABS.DEFAULT_CATEGORIES}
              className="t-px-5"
            >
              <DefaultCategories entityId={entityId!} />
            </Tab.Content>
            <Tab.Content
              value={BOOKS_SETTINGS_TABS.CLASSIFICATIONS}
              className="t-px-5"
            >
              <PermissionBasedUI
                feature={CLASSIFICATION_FEATURE_ACCESS}
                entityId={entityId}
                onUpgrade={subscriptionModal.open}
                blockedUI={
                  <div className="t-flex t-flex-col t-items-center t-justify-center t-gap-4 t-py-56">
                    <Wind className="t-text-neutral-40 t-size-6" />
                    <div className="t-flex t-flex-col t-items-center t-gap-2">
                      <p className="t-text-subtitle-sm t-text-text-60 t-m-0">
                        Classifications are not available
                      </p>
                      <p className="t-text-body-sm t-text-neutral-40 t-m-0">
                        Upgrade your current plan to create and manage.
                      </p>
                    </div>
                    <Button
                      onClick={subscriptionModal.open}
                      customType="primary_outlined"
                      size="small"
                    >
                      Upgrade
                    </Button>
                  </div>
                }
                showTooltip={false}
              >
                <ClassificationsSettings entityId={entityId!} />
              </PermissionBasedUI>
            </Tab.Content>
            <Tab.Content
              value={BOOKS_SETTINGS_TABS.INVOICES}
              className="t-px-5"
            >
              <InvoiceSettings entityId={entityId!} />
            </Tab.Content>
            <Tab.Content value={BOOKS_SETTINGS_TABS.BILLS} className="t-px-5">
              <BillsSettings entityId={entityId!} />
            </Tab.Content>
            <Tab.Content
              value={BOOKS_SETTINGS_TABS.CHART_OF_ACCOUNTS}
              className="t-h-full"
            >
              <ChartOfAccounts isStandalone={false} />
            </Tab.Content>
          </DashboardContainer.Content>
        </DashboardContainer>
      </Tab.Root>
      <BooksSoftwareSubscriptionsModal
        open={subscriptionModal.isOpen}
        onClose={subscriptionModal.close}
        entityId={entityId}
        onProductSubscribe={subscriptionModal.close}
        modalDescription={
          <ModalDescriptionWrapper value="Unlock Classifications and more with Books Standard Plan" />
        }
      />
    </>
  );
};

export default BooksSettings;
